/**
 * RoadColliderManager - Physische Mesh-Kollisionsbarrieren entlang der Straßen
 * 
 * Erstellt unsichtbare Box-Meshes als Barrieren entlang der Road-Polylinien,
 * die echte physische Kollisionen mit dem Spielerfahrzeug ermöglichen.
 */

import * as THREE from 'three';
import type { RoadSegment } from './OpenWorldMapLoader';

export interface ColliderSegment {
  id: string;
  mesh: THREE.Mesh;
  roadId: string;
  side: 'left' | 'right';
}

export class RoadColliderManager {
  private scene: THREE.Scene;
  private colliders: Map<string, ColliderSegment> = new Map();
  private colliderGroup: THREE.Group;
  private colliderMaterial: THREE.MeshBasicMaterial;
  private debugMode = false;
  
  // Collider-Parameter
  private readonly COLLIDER_HEIGHT = 2.5; // Höhe der Barriere
  private readonly COLLIDER_THICKNESS = 0.3; // Dicke der Barriere
  private readonly COLLIDER_OFFSET_MULTIPLIER = 0.52; // Offset von Straßenmitte (52% der Straßenbreite)
  private readonly MIN_SEGMENT_LENGTH = 2; // Minimale Segmentlänge für Collider
  
  constructor(scene: THREE.Scene, debugMode = false) {
    this.scene = scene;
    this.debugMode = debugMode;
    
    // Collider Group für einfaches Management
    this.colliderGroup = new THREE.Group();
    this.colliderGroup.name = 'RoadColliders';
    this.scene.add(this.colliderGroup);
    
    // Material für Collider (transparent wenn nicht debug)
    this.colliderMaterial = new THREE.MeshBasicMaterial({
      color: debugMode ? 0xff0000 : 0x000000,
      transparent: !debugMode,
      opacity: debugMode ? 0.3 : 0,
      side: THREE.DoubleSide,
      depthWrite: false
    });
  }
  
  /**
   * Generiert Collider-Meshes für eine Straße
   */
  generateCollidersForRoad(road: RoadSegment): void {
    // Collider für diese Straße löschen falls bereits vorhanden
    this.removeCollidersForRoad(road.id);
    
    const points = road.points;
    if (points.length < 2) return;
    
    // Für jeden Straßenabschnitt Collider generieren
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      
      const segmentVector = new THREE.Vector3().subVectors(p2, p1);
      const segmentLength = segmentVector.length();
      
      // Überspringe zu kurze Segmente
      if (segmentLength < this.MIN_SEGMENT_LENGTH) continue;
      
      // Segment-Center und Richtung
      const center = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);
      const direction = segmentVector.normalize();
      
      // Rechts-Vektor berechnen (90° rotiert um Y-Achse)
      const right = new THREE.Vector3(-direction.z, 0, direction.x);
      
      // Offset basierend auf Straßenbreite
      const roadWidth = road.width;
      const lateralOffset = roadWidth * this.COLLIDER_OFFSET_MULTIPLIER;
      
      // Linke Barriere
      this.createColliderMesh(
        road.id,
        i,
        'left',
        center,
        direction,
        right,
        lateralOffset,
        segmentLength
      );
      
      // Rechte Barriere
      this.createColliderMesh(
        road.id,
        i,
        'right',
        center,
        direction,
        right,
        -lateralOffset,
        segmentLength
      );
    }
  }
  
  /**
   * Erstellt ein einzelnes Collider-Mesh
   */
  private createColliderMesh(
    roadId: string,
    segmentIndex: number,
    side: 'left' | 'right',
    center: THREE.Vector3,
    direction: THREE.Vector3,
    right: THREE.Vector3,
    lateralOffset: number,
    length: number
  ): void {
    // Box-Geometrie für Barriere
    const geometry = new THREE.BoxGeometry(
      this.COLLIDER_THICKNESS,
      this.COLLIDER_HEIGHT,
      length
    );
    
    const mesh = new THREE.Mesh(geometry, this.colliderMaterial);
    
    // Position: Center + laterales Offset
    const offset = right.clone().multiplyScalar(lateralOffset);
    mesh.position.copy(center).add(offset);
    mesh.position.y = this.COLLIDER_HEIGHT / 2; // Barriere auf Boden
    
    // Rotation: entlang Straßenrichtung
    const angle = Math.atan2(direction.x, direction.z);
    mesh.rotation.y = angle;
    
    // UserData für Kollisionsidentifikation
    mesh.userData.isRoadCollider = true;
    mesh.userData.roadId = roadId;
    mesh.userData.side = side;
    mesh.userData.segmentIndex = segmentIndex;
    
    // Collider speichern
    const colliderId = `${roadId}_${segmentIndex}_${side}`;
    const collider: ColliderSegment = {
      id: colliderId,
      mesh,
      roadId,
      side
    };
    
    this.colliders.set(colliderId, collider);
    this.colliderGroup.add(mesh);
  }
  
  /**
   * Entfernt alle Collider für eine bestimmte Straße
   */
  removeCollidersForRoad(roadId: string): void {
    const toRemove: string[] = [];
    
    for (const [id, collider] of this.colliders.entries()) {
      if (collider.roadId === roadId) {
        this.colliderGroup.remove(collider.mesh);
        collider.mesh.geometry.dispose();
        toRemove.push(id);
      }
    }
    
    toRemove.forEach(id => this.colliders.delete(id));
  }
  
  /**
   * Prüft Kollision mit Collider-Meshes
   * @returns Kollisions-Info oder null wenn keine Kollision
   */
  checkCollision(position: THREE.Vector3, radius = 1.5): {
    collision: boolean;
    normal: THREE.Vector3;
    penetrationDepth: number;
    colliderMesh: THREE.Mesh;
  } | null {
    const boundingBox = new THREE.Box3(
      new THREE.Vector3(position.x - radius, position.y - 1, position.z - radius),
      new THREE.Vector3(position.x + radius, position.y + 2, position.z + radius)
    );
    
    for (const collider of this.colliders.values()) {
      const mesh = collider.mesh;
      
      // Bounding Box Check
      if (!mesh.geometry.boundingBox) {
        mesh.geometry.computeBoundingBox();
      }
      
      const colliderBox = mesh.geometry.boundingBox!.clone();
      colliderBox.applyMatrix4(mesh.matrixWorld);
      
      if (boundingBox.intersectsBox(colliderBox)) {
        // Kollision erkannt - berechne Normale
        const meshPos = mesh.position;
        const toPlayer = new THREE.Vector3().subVectors(position, meshPos);
        toPlayer.y = 0; // Nur horizontale Komponente
        
        const distance = toPlayer.length();
        if (distance < 0.001) {
          // Spieler direkt im Mesh-Center, nutze Side-Info
          const normal = collider.side === 'left' 
            ? new THREE.Vector3(-1, 0, 0) 
            : new THREE.Vector3(1, 0, 0);
          normal.applyQuaternion(mesh.quaternion);
          return {
            collision: true,
            normal: normal.normalize(),
            penetrationDepth: radius,
            colliderMesh: mesh
          };
        }
        
        const normal = toPlayer.normalize();
        const penetration = radius - distance;
        
        return {
          collision: true,
          normal,
          penetrationDepth: Math.max(0, penetration),
          colliderMesh: mesh
        };
      }
    }
    
    return null;
  }
  
  /**
   * Aktualisiert Collider für sichtbare Straßen im Radius
   */
  updateCollidersForRoads(roads: RoadSegment[]): void {
    const currentRoadIds = new Set(roads.map(r => r.id));
    
    // Entferne Collider für nicht mehr sichtbare Straßen
    const toRemove: string[] = [];
    for (const [id, collider] of this.colliders.entries()) {
      if (!currentRoadIds.has(collider.roadId)) {
        toRemove.push(id);
      }
    }
    toRemove.forEach(id => {
      const collider = this.colliders.get(id);
      if (collider) {
        this.colliderGroup.remove(collider.mesh);
        collider.mesh.geometry.dispose();
        this.colliders.delete(id);
      }
    });
    
    // Generiere Collider für neue Straßen
    for (const road of roads) {
      if (!this.hasCollidersForRoad(road.id)) {
        this.generateCollidersForRoad(road);
      }
    }
  }
  
  /**
   * Prüft ob Collider für Straße bereits existieren
   */
  private hasCollidersForRoad(roadId: string): boolean {
    for (const collider of this.colliders.values()) {
      if (collider.roadId === roadId) {
        return true;
      }
    }
    return false;
  }
  
  /**
   * Debug-Modus umschalten
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    this.colliderMaterial.opacity = enabled ? 0.3 : 0;
    this.colliderMaterial.transparent = true;
    this.colliderMaterial.color.setHex(enabled ? 0xff0000 : 0x000000);
    this.colliderMaterial.needsUpdate = true;
  }
  
  /**
   * Gibt Collider-Statistik zurück
   */
  getStats(): { totalColliders: number; totalMeshes: number } {
    return {
      totalColliders: this.colliders.size,
      totalMeshes: this.colliderGroup.children.length
    };
  }
  
  /**
   * Räumt alle Collider auf
   */
  dispose(): void {
    for (const collider of this.colliders.values()) {
      this.colliderGroup.remove(collider.mesh);
      collider.mesh.geometry.dispose();
    }
    this.colliders.clear();
    this.scene.remove(this.colliderGroup);
    this.colliderMaterial.dispose();
  }
}
